import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, Check, Copy, Plug, RefreshCw } from 'lucide-react';
import {
  getUserMcpConfig,
  enableUserMcp,
  regenerateUserMcpToken,
  disableUserMcp,
  setUserMcpWriteEnabled,
  UserMcpConfig
} from '../../services/calendar-mcp';

const MCP_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/mcp/user`;

const READ_TOOLS_LABEL = 'list_bookings, get_booking';
const WRITE_TOOLS_LABEL = 'create_booking, update_booking (facilitator, date, description), cancel_booking';

interface Props {
  userId: string;
}

export const CalendarMcpPanel: React.FC<Props> = ({ userId }) => {
  const [config, setConfig] = useState<UserMcpConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [snippetCopied, setSnippetCopied] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setConfig(await getUserMcpConfig());
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  async function runAction(fn: () => Promise<void>) {
    setWorking(true);
    setError(null);
    setRevealedToken(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleEnable() {
    await runAction(async () => {
      const data = await enableUserMcp();
      setRevealedToken(data.token);
      setConfig(prev => ({ mcpEnabled: true, hasToken: true, mcpTokenHint: data.mcpTokenHint, mcpWriteEnabled: prev?.mcpWriteEnabled ?? false }));
    });
  }

  async function handleRegenerate() {
    await runAction(async () => {
      const data = await regenerateUserMcpToken();
      setRevealedToken(data.token);
      setConfig(prev => (prev ? { ...prev, hasToken: true, mcpTokenHint: data.mcpTokenHint } : prev));
    });
  }

  async function handleDisable() {
    await runAction(async () => {
      await disableUserMcp();
      setConfig(prev => (prev ? { ...prev, mcpEnabled: false, mcpWriteEnabled: false } : prev));
    });
  }

  async function handleToggleWrite(next: boolean) {
    await runAction(async () => {
      await setUserMcpWriteEnabled(next);
      setConfig(prev => (prev ? { ...prev, mcpWriteEnabled: next } : prev));
    });
  }

  function copyToken() {
    if (!revealedToken) return;
    navigator.clipboard.writeText(revealedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const tokenDisplay = revealedToken ?? `ucal_...${config?.mcpTokenHint ?? 'xxxx'}`;
  const configSnippet = `{
  "mcpServers": {
    "v1cc-calendar": {
      "type": "http",
      "url": "${MCP_BASE}/${userId}",
      "headers": {
        "Authorization": "Bearer ${tokenDisplay}"
      }
    }
  }
}`;

  function copySnippet() {
    navigator.clipboard.writeText(configSnippet);
    setSnippetCopied(true);
    setTimeout(() => setSnippetCopied(false), 2000);
  }

  if (loading) {
    return (
      <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
        <div className="flex justify-center items-center py-16 text-slate-400 text-sm">Loading…</div>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <CalendarClock size={18} className="text-primary" />
        </div>
        <div>
          <h3 className="font-bold text-[#052831] text-lg">Calendar MCP</h3>
          <p className="text-sm text-slate-500">
            Personal token — connects Claude Code to Studio room bookings across every project you can see.
          </p>
        </div>
      </div>

      <div className="p-5 flex flex-col gap-5">
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex items-center gap-3">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        {/* Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${config?.mcpEnabled ? 'bg-green-500' : 'bg-slate-300'}`} />
            <span className="text-sm font-semibold text-navy">{config?.mcpEnabled ? 'Enabled' : 'Disabled'}</span>
            <span className="text-sm text-slate-500">
              {config?.mcpEnabled ? 'Claude Code can read your calendar.' : 'Enable to generate a personal access token.'}
            </span>
          </div>
          {config?.mcpEnabled ? (
            <button
              onClick={handleDisable}
              disabled={working}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
            >
              Disable
            </button>
          ) : (
            <button
              onClick={handleEnable}
              disabled={working}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
            >
              <Plug size={16} /> Enable MCP
            </button>
          )}
        </div>

        {config?.mcpEnabled && (
          <>
            {/* Write toggle */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div>
                <p className="text-sm font-semibold text-navy">Write access</p>
                <p className="text-sm text-slate-500">
                  {config.mcpWriteEnabled
                    ? 'Claude Code can update booking facilitator, date, and description.'
                    : 'Claude Code can only read bookings.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggleWrite(!config.mcpWriteEnabled)}
                disabled={working}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${config.mcpWriteEnabled ? 'bg-primary' : 'bg-slate-300'}`}
                role="switch"
                aria-checked={!!config.mcpWriteEnabled}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${config.mcpWriteEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            {/* Token */}
            <div className="pt-4 border-t border-slate-100">
              {revealedToken ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <code className="flex-1 text-xs font-mono text-navy break-all">{revealedToken}</code>
                    <button onClick={copyToken} className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors">
                      {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                    </button>
                  </div>
                  <p className="text-xs text-amber-600 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> Copy this token now — it will not be shown again.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    {config.hasToken ? (
                      <>Token configured — ends in <code className="font-mono text-navy">…{config.mcpTokenHint}</code></>
                    ) : (
                      'No token configured yet.'
                    )}
                  </p>
                  <button
                    onClick={handleRegenerate}
                    disabled={working}
                    className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={14} /> Regenerate Token
                  </button>
                </div>
              )}
            </div>

            {/* Config snippet */}
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Claude Code Config</p>
                <button onClick={copySnippet} className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors">
                  {snippetCopied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Add this to your <code className="font-mono text-xs text-navy">~/.claude/mcp.json</code>:
              </p>
              <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs font-mono text-navy overflow-x-auto whitespace-pre leading-relaxed">
                {configSnippet}
              </pre>
              <p className="text-xs text-slate-400 mt-3">
                Read tools: <code className="font-mono">{READ_TOOLS_LABEL}</code>
              </p>
              {config.mcpWriteEnabled && (
                <p className="text-xs text-slate-400 mt-2">
                  Write tools: <code className="font-mono">{WRITE_TOOLS_LABEL}</code>
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
